import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import spontaneousService from "services/spontaneousService";

const useSpontaneousWithPagination = ({
  searchTerm = '',
  location = '',
  showSnackbar = () => {},
  t = () => {},
  mode = '',
  type = ''
}) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const accessToken = localStorage.getItem('accessToken');
  const navigate = useNavigate();

  const [spontaneousList, setSpontaneousList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false
  });

  const fetchSpontaneous = useCallback(async (page = pagination.currentPage) => {
    setLoading(true);
    setError(null);
    try {
      const data = await spontaneousService.fetchSpontaneous(
        accessToken,
        page,
        apiUrl
      );
      setSpontaneousList(data.spontaneous || []);
      console.log('data.spontaneous =>:', data.spontaneous);
      setPagination({
        currentPage: data.currentPage || page,
        totalPages: data.totalPages || 1,
        hasNextPage: page < (data.totalPages || 1),
        hasPreviousPage: page > 1
      });
    } catch (err) {
      setError(err);
      setSpontaneousList([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken, apiUrl, pagination.currentPage]);

  useEffect(() => {
    fetchSpontaneous(pagination.currentPage);
  }, [fetchSpontaneous, pagination.currentPage]);

  const handleSearch = async (event, page = 1) => {
    if (window.location.pathname.includes('/landing')) {
      navigate('/jobs');
    }

    if (event && event.preventDefault) {
      event.preventDefault();
    }

    if (
      searchTerm.trim() === '' &&
      location.trim() === '' &&
      type.trim() === '' &&
      mode.trim() === ''
    ) {
      showSnackbar(t("searchBox.errors.enter_job_or_city"), "error");
      fetchSpontaneous();
      return;
    }

    setLoading(true);
    try {
      let url = `${apiUrl}posts/search_recommendation/?`;
      if (searchTerm.trim()) {
        url += `title=${encodeURIComponent(searchTerm.trim())}`;
      }
      if (location.trim()) {
        url += `${url.includes("?") ? "&" : "?"}localisation=${encodeURIComponent(location.trim())}`;
      }
      if (mode.trim()) {
        url += `${url.includes("?") ? "&" : "?"}mode=${encodeURIComponent(mode.trim())}`;
      }
      if (type.trim()) {
        url += `${url.includes("?") ? "&" : "?"}offerType=${encodeURIComponent(type.trim())}`;
      }

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      setSpontaneousList(data.spontaneous || []);
      setPagination({
        currentPage: data.currentPage || page,
        totalPages: data.totalPages || 1,
        hasNextPage: page < (data.totalPages || 1),
        hasPreviousPage: page > 1
      });
    } catch (err) {
      setError(err);
      setSpontaneousList([]);
    } finally {
      setLoading(false);
    }
  };

  const goToNextPage = useCallback(() => {
    if (pagination.hasNextPage) {
      setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }));
    }
  }, [pagination.hasNextPage]);

  const goToPreviousPage = useCallback(() => {
    if (pagination.hasPreviousPage) {
      setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }));
    }
  }, [pagination.hasPreviousPage]);

  const goToPage = useCallback((pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: pageNumber }));
    }
  }, [pagination.totalPages]);

  const refreshSpontaneous = useCallback(() => {
    fetchSpontaneous(pagination.currentPage);
  }, [fetchSpontaneous, pagination.currentPage]);

  return {
    spontaneousList,
    loading,
    error,
    pagination,
    goToNextPage,
    goToPreviousPage,
    goToPage,
    refreshSpontaneous,
    handleSearch
  };
};

export default useSpontaneousWithPagination;
